import { getActivePreviewCompany, PREVIEW_ACTOR_ID } from './flag';
import { previewData } from './data';

// Preview Mode emulation of internal tasks (Phase 8.4–8.5, D-219 / migration 0145): the RPCs'
// observable contract — the two-step lattice (OPEN → SUBMITTED → CLOSED, SUBMITTED → OPEN with a
// mandatory comment), forward-only postponement with its counter and original date, the history,
// the server-decided action flags and the sanitized refusal codes. Preview is always the demo
// administrator (company scope, canManage); the seeded tasks are assigned to the demo sales manager,
// so "submit" never appears here — one seeded task is already SUBMITTED to show close / return.
// UI verification only — the real rules live in the database.

type Row = Record<string, unknown>;
type Result<T> = { data: T; error: null | { message: string } };

const MANAGER_ID = 'preview-sales-manager';
const MANAGER_NAME = 'ნინო კაპანაძე';
const ADMIN_NAME = 'დემო ადმინისტრატორი';

interface HistoryEvent {
  readonly kind: 'CREATED' | 'SUBMITTED' | 'RETURNED' | 'POSTPONED' | 'CLOSED';
  readonly at: string;
  readonly actorId: string;
  readonly actorName: string;
  readonly comment: string | null;
  readonly reason: string | null;
  readonly fromDate: string | null;
  readonly toDate: string | null;
}

interface PreviewTask {
  readonly taskId: string;
  readonly companyId: string;
  readonly customerId: string;
  readonly text: string;
  status: 'OPEN' | 'SUBMITTED' | 'CLOSED';
  dueDate: string | null;
  readonly originalDueDate: string | null;
  postponedCount: number;
  returnedCount: number;
  readonly createdAt: string;
  submittedAt: string | null;
  closedAt: string | null;
  readonly history: HistoryEvent[];
}

const tasks: PreviewTask[] = [];
const seeded = new Set<string>();
const replays = new Map<string, { hash: string; result: Row }>();

function text(v: unknown): string {
  return typeof v === 'string' ? v : '';
}
function today(): string {
  return new Date().toISOString().slice(0, 10);
}
function shift(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
function customers(companyId: string): Row[] {
  return (previewData(companyId).tables['customers'] as Row[] | undefined) ?? [];
}
function event(kind: HistoryEvent['kind'], at: string, actorId: string, actorName: string, extra: Partial<HistoryEvent> = {}): HistoryEvent {
  return { kind, at, actorId, actorName, comment: null, reason: null, fromDate: null, toDate: null, ...extra };
}

/** Three demo tasks per company, written once: overdue + postponed twice, submitted, returned. */
function seed(companyId: string): void {
  if (seeded.has(companyId)) return;
  seeded.add(companyId);
  const cs = customers(companyId);
  const pick = (i: number): string => text(cs[i % Math.max(cs.length, 1)]?.['id']);
  if (cs.length === 0) return;
  const t0 = today();
  const orig = shift(t0, -12);
  const p1 = shift(t0, -8);
  const p2 = shift(t0, -2);
  tasks.push(
    {
      taskId: `it-${companyId}-1`, companyId, customerId: pick(0), text: 'დაურეკე ბუღალტერიას და დააზუსტე აგვისტოს ზედნადებების ჩაბარება.',
      status: 'OPEN', dueDate: p2, originalDueDate: orig, postponedCount: 2, returnedCount: 0,
      createdAt: `${shift(t0, -15)}T08:00:00.000Z`, submittedAt: null, closedAt: null,
      history: [
        event('CREATED', `${shift(t0, -15)}T08:00:00.000Z`, PREVIEW_ACTOR_ID, ADMIN_NAME, { toDate: orig }),
        event('POSTPONED', `${shift(t0, -12)}T10:00:00.000Z`, MANAGER_ID, MANAGER_NAME, { fromDate: orig, toDate: p1, reason: 'ბუღალტერი შვებულებაშია' }),
        event('POSTPONED', `${shift(t0, -8)}T09:30:00.000Z`, MANAGER_ID, MANAGER_NAME, { fromDate: p1, toDate: p2 }),
      ],
    },
    {
      taskId: `it-${companyId}-2`, companyId, customerId: pick(1), text: 'შეხვედრა დირექტორთან — დავალიანების გრაფიკის შეთანხმება.',
      status: 'SUBMITTED', dueDate: shift(t0, 3), originalDueDate: shift(t0, 3), postponedCount: 0, returnedCount: 0,
      createdAt: `${shift(t0, -5)}T07:00:00.000Z`, submittedAt: `${shift(t0, -1)}T12:00:00.000Z`, closedAt: null,
      history: [
        event('CREATED', `${shift(t0, -5)}T07:00:00.000Z`, PREVIEW_ACTOR_ID, ADMIN_NAME, { toDate: shift(t0, 3) }),
        event('SUBMITTED', `${shift(t0, -1)}T12:00:00.000Z`, MANAGER_ID, MANAGER_NAME),
      ],
    },
    {
      taskId: `it-${companyId}-3`, companyId, customerId: pick(2), text: 'მოითხოვე შედარების აქტი ხელმოწერით.',
      status: 'OPEN', dueDate: null, originalDueDate: null, postponedCount: 0, returnedCount: 1,
      createdAt: `${shift(t0, -4)}T09:00:00.000Z`, submittedAt: null, closedAt: null,
      history: [
        event('CREATED', `${shift(t0, -4)}T09:00:00.000Z`, PREVIEW_ACTOR_ID, ADMIN_NAME),
        event('SUBMITTED', `${shift(t0, -3)}T15:00:00.000Z`, MANAGER_ID, MANAGER_NAME),
        event('RETURNED', `${shift(t0, -2)}T08:30:00.000Z`, PREVIEW_ACTOR_ID, ADMIN_NAME, { comment: 'აქტი ხელმოწერის გარეშეა — საჭიროა ბეჭდით.' }),
      ],
    },
  );
}

function toJson(t: PreviewTask): Row {
  const c = customers(t.companyId).find((x) => x['id'] === t.customerId);
  const lastReturn = [...t.history].reverse().find((h) => h.kind === 'RETURNED');
  return {
    taskId: t.taskId, customerId: t.customerId, customerName: c?.['name'] ?? null, taxId: text(c?.['tax_id']),
    assigneeUserId: MANAGER_ID, assigneeName: MANAGER_NAME, text: t.text, status: t.status,
    dueDate: t.dueDate, originalDueDate: t.originalDueDate, postponedCount: t.postponedCount, returnedCount: t.returnedCount,
    overdue: t.status === 'OPEN' && t.dueDate !== null && t.dueDate < today(),
    createdAt: t.createdAt, createdById: PREVIEW_ACTOR_ID, createdByName: ADMIN_NAME,
    submittedAt: t.submittedAt, closedAt: t.closedAt,
    lastReturnComment: t.returnedCount > 0 ? lastReturn?.comment ?? null : null,
    canSubmit: false,
    canPostpone: t.status === 'OPEN' && t.dueDate !== null,
    canClose: t.status === 'SUBMITTED',
    canReturn: t.status === 'SUBMITTED',
  };
}

const rank = (t: PreviewTask): [number, number, string, string] => [
  t.status === 'CLOSED' ? 1 : 0, t.dueDate === null ? 1 : 0, t.dueDate ?? '', t.createdAt,
];
function byServerOrder(a: PreviewTask, b: PreviewTask): number {
  const [a0, a1, a2, a3] = rank(a);
  const [b0, b1, b2, b3] = rank(b);
  return a0 - b0 || a1 - b1 || (a2 < b2 ? -1 : a2 > b2 ? 1 : 0) || (a3 < b3 ? 1 : a3 > b3 ? -1 : 0);
}

const fail = (message: string): Result<null> => ({ data: null, error: { message } });

/** Idempotent write: a replayed key with the same request returns the first result. */
function once(companyId: string, payload: Row, hash: string, act: () => Result<Row | null>): Result<Row | null> {
  const k = `${companyId}:${text(payload['idempotencyKey']).trim()}`;
  if (text(payload['idempotencyKey']).trim() === '') return fail('INTERNAL_TASK_INVALID_PAYLOAD');
  const prior = replays.get(k);
  if (prior !== undefined) return prior.hash === hash ? { data: prior.result, error: null } : fail('IDEMPOTENCY_CONFLICT');
  const r = act();
  if (r.error === null && r.data !== null) replays.set(k, { hash, result: r.data });
  return r;
}

const TASK_FNS = new Set([
  'fn_internal_task_list', 'fn_internal_task_detail', 'fn_internal_task_assignment_options', 'fn_internal_task_create',
  'fn_internal_task_submit', 'fn_internal_task_close', 'fn_internal_task_return', 'fn_internal_task_postpone',
]);

/** The 0145 RPCs; `undefined` for any other function. */
export function previewTaskRpc(fn: string, args: Row): Result<unknown> | undefined {
  if (!TASK_FNS.has(fn)) return undefined;
  const companyId = text(args['p_company']) || getActivePreviewCompany();
  const payload = (args['p_payload'] ?? {}) as Row;
  seed(companyId);
  const mine = tasks.filter((t) => t.companyId === companyId);
  const find = (): PreviewTask | undefined => mine.find((t) => t.taskId === text(payload['taskId']));
  const now = new Date().toISOString();

  switch (fn) {
    case 'fn_internal_task_list': {
      const f = (payload['filters'] ?? {}) as Row;
      const status = text(f['status']);
      const items = mine
        .filter((t) => status === '' || (status === 'ACTIVE' ? t.status !== 'CLOSED' : t.status === status))
        .filter((t) => f['overdueOnly'] !== true || (t.status === 'OPEN' && t.dueDate !== null && t.dueDate < today()))
        .sort(byServerOrder)
        .map(toJson);
      const count = (s: string): number => mine.filter((t) => t.status === s).length;
      return {
        data: {
          ok: true, scope: 'company', canManage: true, items, truncated: false,
          counts: {
            open: count('OPEN'), submitted: count('SUBMITTED'), closed: count('CLOSED'),
            overdue: mine.filter((t) => t.status === 'OPEN' && t.dueDate !== null && t.dueDate < today()).length,
          },
        },
        error: null,
      };
    }
    case 'fn_internal_task_detail': {
      const t = find();
      return t === undefined ? fail('INTERNAL_TASK_NOT_FOUND') : { data: { ok: true, task: toJson(t), history: t.history }, error: null };
    }
    case 'fn_internal_task_assignment_options':
      return {
        data: {
          ok: true, truncated: false,
          customers: customers(companyId).map((c) => ({
            customerId: c['id'], customerName: c['name'] ?? null, taxId: text(c['tax_id']), assigneeUserId: MANAGER_ID, assigneeName: MANAGER_NAME,
          })),
        },
        error: null,
      };
    case 'fn_internal_task_create': {
      const body = text(payload['text']).trim();
      const customerId = text(payload['customerId']);
      const due = text(payload['dueDate']) || null;
      if (customerId === '') return fail('INTERNAL_TASK_CUSTOMER_REQUIRED');
      if (body === '' || body.length > 2000) return fail('INTERNAL_TASK_INVALID_PAYLOAD');
      if (!customers(companyId).some((c) => c['id'] === customerId)) return fail('INTERNAL_TASK_CUSTOMER_NOT_FOUND');
      return once(companyId, payload, `create:${customerId}:${due ?? ''}:${body}`, () => {
        const t: PreviewTask = {
          taskId: `it-preview-${tasks.length + 1}`, companyId, customerId, text: body, status: 'OPEN',
          dueDate: due, originalDueDate: due, postponedCount: 0, returnedCount: 0, createdAt: now,
          submittedAt: null, closedAt: null,
          history: [event('CREATED', now, PREVIEW_ACTOR_ID, ADMIN_NAME, { toDate: due })],
        };
        tasks.push(t);
        return { data: { ok: true, taskId: t.taskId, status: 'OPEN' }, error: null };
      });
    }
    case 'fn_internal_task_submit':
      // Preview's actor is the administrator — never an assignee.
      return find() === undefined ? fail('INTERNAL_TASK_NOT_FOUND') : fail('INTERNAL_TASK_NOT_ASSIGNEE');
    case 'fn_internal_task_close': {
      const t = find();
      if (t === undefined) return fail('INTERNAL_TASK_NOT_FOUND');
      return once(companyId, payload, `close:${t.taskId}`, () => {
        if (t.status === 'OPEN') return fail('INTERNAL_TASK_NOT_SUBMITTED');
        if (t.status === 'CLOSED') return fail('INTERNAL_TASK_ALREADY_CLOSED');
        t.status = 'CLOSED';
        t.closedAt = now;
        t.history.push(event('CLOSED', now, PREVIEW_ACTOR_ID, ADMIN_NAME));
        return { data: { ok: true, taskId: t.taskId, status: 'CLOSED' }, error: null };
      });
    }
    case 'fn_internal_task_return': {
      const t = find();
      const comment = text(payload['comment']).trim();
      if (comment === '') return fail('INTERNAL_TASK_RETURN_REASON_REQUIRED');
      if (t === undefined) return fail('INTERNAL_TASK_NOT_FOUND');
      return once(companyId, payload, `return:${t.taskId}:${comment}`, () => {
        if (t.status === 'OPEN') return fail('INTERNAL_TASK_NOT_SUBMITTED');
        if (t.status === 'CLOSED') return fail('INTERNAL_TASK_ALREADY_CLOSED');
        t.status = 'OPEN';
        t.submittedAt = null;
        t.returnedCount += 1;
        t.history.push(event('RETURNED', now, PREVIEW_ACTOR_ID, ADMIN_NAME, { comment }));
        return { data: { ok: true, taskId: t.taskId, status: 'OPEN', returnedCount: t.returnedCount }, error: null };
      });
    }
    case 'fn_internal_task_postpone': {
      const t = find();
      const next = text(payload['newDueDate']);
      const reason = text(payload['reason']).trim() || null;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(next)) return fail('INTERNAL_TASK_INVALID_PAYLOAD');
      if (t === undefined) return fail('INTERNAL_TASK_NOT_FOUND');
      return once(companyId, payload, `postpone:${t.taskId}:${next}:${reason ?? ''}`, () => {
        if (t.status !== 'OPEN') return fail('INTERNAL_TASK_NOT_OPEN');
        if (t.dueDate === null) return fail('INTERNAL_TASK_NO_DUE_DATE');
        if (next <= t.dueDate) return fail('INTERNAL_TASK_POSTPONE_NOT_FORWARD');
        t.history.push(event('POSTPONED', now, PREVIEW_ACTOR_ID, ADMIN_NAME, { fromDate: t.dueDate, toDate: next, reason }));
        t.dueDate = next;
        t.postponedCount += 1;
        return { data: { ok: true, taskId: t.taskId, dueDate: next, postponedCount: t.postponedCount }, error: null };
      });
    }
  }
  return undefined;
}
